import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import LanyardCard from "../../components/LanyardCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label as UILabel } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

const ACCENTS = ["#4338CA", "#0F172A", "#10B981", "#B45309", "#DB2777", "#0369A1"];

export default function PrintLanyard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [org, setOrg] = useState(null);
  const [qr, setQr] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [opts, setOpts] = useState({ tagline: "Scan & rate my service", accent: "#4338CA", show_logo: true, show_role: true, show_photo: false });

  useEffect(() => {
    api.get(`/employees/${id}`).then((r) => {
      const e = r.data.employee;
      setEmp(e);
      setDisplayName((e.name || "").split(" ")[0]);
    }).catch(() => {});
    api.get("/organization").then((r) => setOrg(r.data.organization)).catch(() => {});
    api.get(`/employees/${id}/qr?fmt=png`).then((r) => setQr(r.data)).catch(() => {});
    api.get("/print-template").then((r) => setOpts(r.data)).catch(() => {});
  }, [id]);

  const set = (k) => (e) => setOpts({ ...opts, [k]: e?.target ? e.target.value : e });

  const saveDefault = async () => {
    try { await api.patch("/print-template", opts); toast.success("Saved as the default card layout"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!emp) return <div className="flex min-h-screen items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:px-8">
        <button onClick={() => navigate(`/app/employees/${id}`)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="print-back">
          <ArrowLeft className="h-4 w-4" /> Back to employee
        </button>
        <Button onClick={() => window.print()} data-testid="print-button" className="bg-[#4338CA] hover:bg-[#3730A3]">
          <Printer className="mr-1 h-4 w-4" /> Print
        </Button>
      </div>

      <div className="mx-auto grid max-w-5xl gap-8 p-6 lg:grid-cols-[320px_1fr]">
        <div className="no-print space-y-4 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-head text-lg font-bold text-slate-900">Customise card</h2>
          <div>
            <UILabel>Display name</UILabel>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="print-name" className="mt-1" />
          </div>
          <div>
            <UILabel>Tagline</UILabel>
            <Input value={opts.tagline} onChange={set("tagline")} data-testid="print-tagline" className="mt-1" />
          </div>
          <div>
            <UILabel>Accent colour</UILabel>
            <div className="mt-2 flex gap-2">
              {ACCENTS.map((c) => (
                <button key={c} onClick={() => setOpts({ ...opts, accent: c })} data-testid={`accent-${c}`}
                  className={`h-7 w-7 rounded-full ring-2 ring-offset-2 transition-transform hover:scale-110 ${opts.accent === c ? "ring-slate-900" : "ring-transparent"}`}
                  style={{ background: c }} aria-label={c} />
              ))}
            </div>
          </div>
          <Row label="Show organisation logo"><Switch checked={opts.show_logo} onCheckedChange={set("show_logo")} data-testid="toggle-logo" /></Row>
          <Row label="Show role / job title"><Switch checked={opts.show_role} onCheckedChange={set("show_role")} data-testid="toggle-role" /></Row>
          <Row label={emp.photo_url ? "Show employee photo" : "Show employee photo (no photo on file)"}>
            <Switch checked={opts.show_photo} disabled={!emp.photo_url} onCheckedChange={set("show_photo")} data-testid="toggle-photo" />
          </Row>
          {user?.role === "corporate_admin" && (
            <Button variant="outline" onClick={saveDefault} data-testid="save-template" className="w-full">
              <Save className="mr-1 h-4 w-4" /> Save as default layout
            </Button>
          )}
          <p className="text-xs text-slate-400">Sized as a standard badge insert with bleed, safe area and QR quiet zone. Saved layout applies to single and bulk printing.</p>
        </div>

        <div className="flex justify-center">
          <div className="print-sheet rounded-2xl bg-white p-6 shadow-sm">
            <LanyardCard displayName={displayName} jobTitle={emp.job_title} photoUrl={emp.photo_url}
              qrPng={qr?.png} orgName={org?.name} orgLogo={org?.logo_url} opts={opts} testId="lanyard-card" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
    </div>
  );
}
